/**
 * MKS-351 / MKS-314 — the ONE place in the SPA that decides how a date and a
 * number are written.
 *
 * Date and number formatting are called from ~75 plain functions (`formatDate`
 * in table cells, chart tick formatters, `formatMoney`, `toDateInputText`
 * inside the picker), none of which can call a hook. So both are resolved once
 * here, at the top of `<App>`, and written into the module-level active values
 * in `utils/dateFormat` and `utils/localeFormat` that every one of those
 * functions reads.
 *
 * The write happens during render, not in an effect, on purpose: `<App>` is an
 * ancestor of every date- and money-rendering component, so they all render
 * later in the same pass and all see the same spec. An effect would land one
 * render too late and paint the first resolved frame in the old format. The
 * writes are idempotent and derived only from the flags and the setting, so
 * re-running them is a no-op.
 *
 * MKS-314 added the instance-locale branch, which is what MKS-351's own header
 * said would happen ("this hook is where the chosen locale's spec gets
 * resolved — the call sites stay untouched"). Keeping it in this hook rather
 * than a second one is deliberate: two hooks both calling
 * `setActiveDateFormat` during the same render would make the rendered format
 * depend on which one `<App>` happened to call last.
 *
 * Precedence, and why:
 *   1. MKS-314 flag ON and the instance settings have loaded → the instance's
 *      locale decides BOTH the date spec and the number separators. The date
 *      spec is one of the four EXPLICIT format options in `utils/dateFormat`
 *      (European, German, UK, US), looked up by locale — not read out of ICU.
 *      Probing `Intl` was the staging defect this follow-up fixes: a `cs-CZ`
 *      instance got Czech conventions back and rendered `1. 10. 2026` and
 *      `říjen`, which is what the ticket had set out to remove.
 *   2. Otherwise → exactly MKS-351's behaviour, unchanged: its own flag picks
 *      between the European and the legacy Czech spec, and numbers stay on
 *      `cs-CZ`. This is the MKS-314 flag's OFF branch, and it is byte-identical
 *      to what production renders today.
 *
 * Both flags fail CLOSED. While either query is loading, for an
 * unauthenticated visitor, and on error, `useFlag` answers false and the
 * instance settings are undefined — so the app renders today's output rather
 * than flickering through a second format.
 */
import { useFlag } from '../services/flagsContext';
import { useInstanceSettings } from './useInstanceSettings';
import {
  EUROPEAN_DATE_FORMAT,
  LEGACY_CZ_DATE_FORMAT,
  dateFormatForLocale,
  getActiveDateFormat,
  setActiveDateFormat,
  type DateFormatSpec,
} from '../utils/dateFormat';
import { setActiveNumberLocale } from '../utils/localeFormat';

export function useDateFormatSync(): DateFormatSpec {
  const european = useFlag('mks_351_european_date_format');
  const companyLocaleEnabled = useFlag('mks_314_company_currency_locale');

  // The query is gated on the flag, not only on auth: with the flag off this
  // ticket must cost production zero extra requests.
  const { data: instanceSettings } = useInstanceSettings(companyLocaleEnabled);
  const locale = companyLocaleEnabled ? instanceSettings?.locale : undefined;

  const spec = locale
    ? dateFormatForLocale(locale)
    : european
      ? EUROPEAN_DATE_FORMAT
      : LEGACY_CZ_DATE_FORMAT;

  // Every branch now yields one of the shared spec CONSTANTS — including
  // `dateFormatForLocale`, which since the MKS-351 follow-up returns the
  // option's constant rather than building a fresh object out of `Intl`. So a
  // single identity check guards all of them, and a re-render with an
  // unchanged locale writes nothing.
  if (getActiveDateFormat() !== spec) setActiveDateFormat(spec);

  // `null` — not a locale string — hands every call site back to ITS OWN
  // pre-ticket locale. The codebase had two (`cs-CZ` on the older surfaces,
  // `en-US` on the newer ones), so a single string default here would change
  // what production renders while this flag is still off.
  setActiveNumberLocale(locale ?? null);

  return spec;
}

/**
 * Test seam kept for the suites that call it. The hook holds no state of its
 * own any more — resetting the sync is now exactly `setActiveDateFormat(
 * LEGACY_CZ_DATE_FORMAT)`, which every one of those suites already does.
 */
export function resetDateFormatSyncForTests(): void {
  /* no state to reset */
}
